// src/components/NotificationBell.tsx
// Колокольчик с React Portal — dropdown рендерится в document.body (не обрезается)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, CheckCheck, X } from 'lucide-react';
import api from '../api/client';

const translateNotificationText = (text: string) => {
  if (!text) return text;
  return text
    .replace(/\s*·\s*cancelled/gi, ' · отменено')
    .replace(/\s*·\s*created/gi, ' · создано')
    .replace(/\s*·\s*completed/gi, ' · завершено')
    .replace(/\s*·\s*rescheduled/gi, ' · перенесено')
    .replace(/\s*·\s*updated/gi, ' · обновлено')
    .replace(/\s*·\s*deleted/gi, ' · удалено');
};



interface Notification {
  _id: string;
  module: string;
  type: string;
  action: string;
  entityName: string;
  entityId?: string;
  read: boolean;
  createdAt: string;
}

interface Props {
  module: 'sales' | 'accounting' | 'hrdocs' | 'hrm' | 'hr';
  types?: string | string[];   // ★ Изоляция: types=['elearning'] — только обучение
  accentColor?: string;
  buttonStyle?: React.CSSProperties;
  onItemClick?: (notif: Notification) => void;
}

const MODULE_LABELS: Record<string, string> = {
  sales: 'СРМ Продажи',
  accounting: 'Бухгалтерия',
  hrdocs: 'КДП',
  hrm: 'HRM',
  hr: 'Обучение',
};

const NotificationBell: React.FC<Props> = ({
  module,
  types,
  accentColor = '#2575fc',
  buttonStyle,
  onItemClick,
}) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      // ★ Изоляция по types
      const typesParam = types
        ? `&types=${Array.isArray(types) ? types.join(',') : types}`
        : '';
      const [listRes, countRes] = await Promise.all([
        api.get(`/notifications?module=${module}${typesParam}&limit=30`),
        api.get(`/notifications/count?module=${module}${typesParam}`),
      ]);
      setItems(listRes.data || []);
      setUnread(countRes.data?.count || 0);
    } catch (e) {
      console.error('Notification load error:', e);
    }
  }, [module, types]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  // Расчёт позиции dropdown
  const updateCoords = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 380;
    const margin = 8;

    // По умолчанию — справа от кнопки
    let left = rect.right + margin;

    // Если не влезает — слева
    if (left + dropdownWidth > window.innerWidth - 8) {
      left = rect.left - dropdownWidth - margin;
    }

    // Если и слева не влезает — прижимаем к правому краю
    if (left < 8) {
      left = Math.min(window.innerWidth - dropdownWidth - 8, rect.left);
    }

    // Не вылезаем за левый край
    if (left < 8) left = 8;

    // По вертикали — под кнопкой
    let top = rect.bottom + margin;
    const dropdownMaxHeight = 480;
    if (top + dropdownMaxHeight > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - dropdownMaxHeight - 8);
    }

    setCoords({ top, left });
  }, []);

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }

    // Небольшая задержка, чтобы кнопка уже была отрисована
    const raf = requestAnimationFrame(() => updateCoords());

    const handleScroll = () => updateCoords();
    const handleResize = () => updateCoords();
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, updateCoords]);

  const handleOpen = () => {
    if (!open) load();
    setOpen(!open);
  };

  const handleMarkRead = async (id: string) => {
    try {
      await api.put(`/notifications/read/${id}`);
      setItems(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put(`/notifications/read-all?module=${module}`);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) {}
  };

  const handleItemClick = async (notif: Notification) => {
    if (!notif.read) await handleMarkRead(notif._id);
    if (onItemClick) onItemClick(notif);
    setOpen(false);
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'только что';
    if (mins < 60) return `${mins} мин назад`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} ч назад`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} дн назад`;
    return d.toLocaleDateString('ru-RU');
  };

  // Dropdown через Portal — рендерится в document.body поверх ВСЕГО
  const dropdown = open && coords ? createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        width: 380,
        maxHeight: 480,
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 25px 60px rgba(15,23,42,0.35), 0 4px 12px rgba(0,0,0,0.1)',
        border: '1px solid rgba(226,232,240,0.8)',
        zIndex: 2147483647, // максимальный z-index
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'bellDropdownIn 0.15s ease-out',
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '14px 18px',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: `${accentColor}10`,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bell size={16} color={accentColor} />
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>
            {MODULE_LABELS[module]}
          </div>
          {unread > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700,
              padding: '2px 6px', borderRadius: 8,
              background: accentColor, color: '#fff',
            }}>{unread}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {unread > 0 && (
            <button
              onClick={handleMarkAllRead}
              title="Прочитать все"
              style={{
                background: 'transparent', border: 'none',
                cursor: 'pointer', padding: 6,
                color: '#64748b', borderRadius: 6,
                display: 'flex', alignItems: 'center',
              }}
            >
              <CheckCheck size={14} />
            </button>
          )}
          <button
            onClick={() => setOpen(false)}
            style={{
              background: 'transparent', border: 'none',
              cursor: 'pointer', padding: 6,
              color: '#64748b', borderRadius: 6,
              display: 'flex', alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {items.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            <Bell size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
            <div style={{ fontSize: 13 }}>Нет уведомлений</div>
          </div>
        ) : (
          items.map(n => (
            <div
              key={n._id}
              onClick={() => handleItemClick(n)}
              style={{
                padding: '12px 18px',
                borderBottom: '1px solid #f8fafc',
                cursor: 'pointer',
                background: n.read ? '#fff' : `${accentColor}08`,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={(e) => e.currentTarget.style.background = n.read ? '#fff' : `${accentColor}08`}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: 4,
                  background: n.read ? '#cbd5e1' : accentColor,
                  flexShrink: 0,
                  marginTop: 6,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13,
                    fontWeight: n.read ? 500 : 700,
                    color: '#1a1a2e',
                    lineHeight: 1.4,
                    marginBottom: 2,
                    wordBreak: 'break-word',
                  }}>
                    {translateNotificationText(n.entityName)}
                  </div>
                  <div style={{
                    fontSize: 11, color: '#94a3b8',
                    display: 'flex', gap: 8, alignItems: 'center',
                    flexWrap: 'wrap',
                  }}>
                    <span>{formatTime(n.createdAt)}</span>
                    {n.action === 'approval_required' && (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: '#f59e0b',
                        padding: '1px 6px', borderRadius: 6,
                        background: '#f59e0b15',
                      }}>
                        ТРЕБУЕТ ДЕЙСТВИЯ
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        style={{
          position: 'relative',
          background: 'rgba(255,255,255,0.7)',
          border: '1px solid rgba(255,255,255,0.8)',
          borderRadius: 12,
          padding: 10,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: accentColor,
          transition: 'all 0.15s',
          flexShrink: 0,
          ...(buttonStyle || {}),
        }}
        title={`Уведомления ${MODULE_LABELS[module] || ''}`}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span style={{
            position: 'absolute',
            top: -4,
            right: -4,
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            minWidth: 18,
            height: 18,
            borderRadius: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 5px',
            boxShadow: '0 2px 6px rgba(239,68,68,0.5)',
            pointerEvents: 'none',
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {dropdown}
    </>
  );
};

// Keyframes
if (typeof document !== 'undefined' && !document.getElementById('bell-keyframes')) {
  const style = document.createElement('style');
  style.id = 'bell-keyframes';
  style.textContent = `
    @keyframes bellDropdownIn {
      from { opacity: 0; transform: translateY(-6px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

export default NotificationBell;
